import type { Except, Optional } from '@festina-life/core'
import cx from 'classnames'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import Markdown, { type Options } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { typographyStyles } from '../structure/typography/styles'
import { MarkdownCode } from './MarkdownCode'
import { MarkdownMedia } from './MarkdownMedia'

type CustomLinkType = {
  href?: string
  children?: ReactNode
}

const CustomLink = ({ href, children }: CustomLinkType) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

const WrappedTable = ({
  children,
  ...props
}: ComponentPropsWithoutRef<'table'>) => (
  <div className="overflow-x-auto">
    <table {...props}>{children}</table>
  </div>
)

const MarkdownH1 = (props: ComponentPropsWithoutRef<'h1'>) => (
  <h1
    {...props}
    className={cx(
      typographyStyles({ size: '2xl' }),
      'mt-6 mb-3',
      props.className,
    )}
  />
)

const MarkdownH2 = (props: ComponentPropsWithoutRef<'h2'>) => (
  <h2
    {...props}
    className={cx(
      typographyStyles({ size: 'xl' }),
      'mt-6 mb-2.5',
      props.className,
    )}
  />
)

const MarkdownH3 = (props: ComponentPropsWithoutRef<'h3'>) => (
  <h3
    {...props}
    className={cx(
      typographyStyles({ size: 'lg' }),
      'mt-5 mb-2',
      props.className,
    )}
  />
)

const MarkdownH4 = (props: ComponentPropsWithoutRef<'h4'>) => (
  <h4
    {...props}
    className={cx(
      typographyStyles({ size: 'base' }),
      'mt-4 mb-2',
      props.className,
    )}
  />
)

export type MarkdownViewerProps = Except<Options, 'children'> & {
  children?: Optional<string>
  className?: Optional<string>
  shouldOpenLinksInCurrentTab?: Optional<boolean>
  linkTransformer?: Optional<(content: Optional<string>) => Optional<string>>
  isTrusted?: Optional<boolean>
}

export const MarkdownViewer = ({
  children,
  className,
  shouldOpenLinksInCurrentTab,
  linkTransformer,
  isTrusted,
  ...markdownProps
}: MarkdownViewerProps) => (
  <div
    className={cx(
      'prose dark:prose-invert text-base! [&_pre]:p-0',
      '[&_p]:leading-6 [&_p]:mb-3 [&_p]:mt-0',
      '[&_li]:leading-7 [&_li]:mb-1',
      '[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
      '[&_ul]:my-3 [&_ol]:my-3',
      '[&_table]:w-full [&_table]:border-collapse [&_table]:my-4',
      '[&_th]:border [&_th]:border-slate-200 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:align-top [&_th]:font-semibold [&_th]:bg-slate-50',
      '[&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top',
      'dark:[&_th]:border-slate-700 dark:[&_th]:bg-slate-800/50 dark:[&_td]:border-slate-700',
      '[&_hr]:my-6',
      className,
    )}
  >
    <Markdown
      {...markdownProps}
      components={{
        img: MarkdownMedia,
        code: MarkdownCode,
        table: WrappedTable,
        h1: MarkdownH1,
        h2: MarkdownH2,
        h3: MarkdownH3,
        h4: MarkdownH4,
        ...(!shouldOpenLinksInCurrentTab ? { a: CustomLink } : {}),
        ...markdownProps.components,
      }}
      remarkPlugins={[remarkGfm, ...(markdownProps.remarkPlugins ?? [])]}
      // Rehype raw is required for safe HTML rendering. Should only be used if the markdown is trusted.
      rehypePlugins={[
        ...(isTrusted ? [rehypeRaw] : []),
        ...(markdownProps.rehypePlugins ?? []),
      ]}
    >
      {linkTransformer ? linkTransformer(children) : children}
    </Markdown>
  </div>
)
